import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';
import { TenantCan } from '../components/tenant/TenantCan';

function statusColor(s) {
  if (s === 'active') return 'success';
  if (s === 'paused') return 'warning';
  return 'default';
}

export default function TenantWebhookDetailPage() {
  const { webhookId } = useParams();
  const navigate = useNavigate();
  const [webhook, setWebhook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/webhooks/${webhookId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setWebhook(json.webhook);
    } catch (e) {
      setError(e.message);
      setWebhook(null);
    } finally {
      setLoading(false);
    }
  }, [webhookId]);

  useEffect(() => {
    load();
  }, [load]);

  const doDelete = async () => {
    setDeleting(true);
    try {
      const res = await tenantApiFetch(`/api/tenant/webhooks/${webhookId}`, { method: 'DELETE' });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Delete failed');
      }
      navigate('/tenant/webhooks');
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const doTest = async () => {
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/webhooks/${webhookId}/test`, { method: 'POST', body: {} });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Test failed');
      setTestResult(json);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !webhook) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!webhook) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
        {value ?? '—'}
      </Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">{webhook.webhook_name}</Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={webhook.status} color={statusColor(webhook.status)} />
            <Chip size="small" label={webhook.secret_masked} variant="outlined" sx={{ fontFamily: 'monospace' }} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TenantCan permission="tenant.webhooks.manage">
            <Button variant="outlined" onClick={doTest} disabled={testing || webhook.status !== 'active'}>
              {testing ? 'Testing…' : 'Send test'}
            </Button>
            <Button variant="outlined" component={Link} to={`/tenant/webhooks/${webhookId}/edit`}>
              Edit
            </Button>
            <Button color="error" variant="outlined" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          </TenantCan>
          <Button variant="outlined" component={Link} to="/tenant/webhooks">
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {testResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setTestResult(null)}>
          HTTP {testResult.httpStatus}
          {testResult.responseSnippet ? ` — ${testResult.responseSnippet.slice(0, 200)}` : ''}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {row('Target URL', webhook.target_url)}
          {row('Secret (masked)', webhook.secret_masked)}
          {row('Event types', (webhook.event_types || []).join(', ') || '—')}
          {row('Retry policy', JSON.stringify(webhook.retry_policy || {}))}
          {row('Last triggered', webhook.last_triggered_at ? String(webhook.last_triggered_at) : '—')}
          {row('Created', webhook.created_at ? String(webhook.created_at) : '—')}
          {row('Updated', webhook.updated_at ? String(webhook.updated_at) : '—')}
        </Grid>
      </Paper>

      <Typography variant="body2" color="text.secondary">
        Test and live deliveries send JSON with headers X-Webhook-Id, X-Webhook-Timestamp, and X-Webhook-Signature:
        sha256=&lt;hmac-sha256(secret, timestamp + &apos;.&apos; + rawBody)&gt;.
      </Typography>

      <Dialog open={confirmDelete} onClose={() => !deleting && setConfirmDelete(false)}>
        <DialogTitle>Delete this webhook?</DialogTitle>
        <DialogContent>
          <DialogContentText>Endpoints that depend on this URL will stop receiving events.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={doDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
